// Diverging waterfall: signed contributions floating from a baseline to a final
// value (green up / red down). Used for forecast feature-attribution and the
// trading net-spread breakdown. Built directly on recharts with a transparent
// base bar + per-bar Cell colouring.

import { memo, useMemo } from "react";
import { Bar, BarChart, Cell, CartesianGrid, Tooltip, XAxis, YAxis } from "recharts";

import { ChartFrame } from "@/components/charts/ChartFrame";
import {
  ANIMATION_ACTIVE,
  AXIS_TICK,
  GRID_PROPS,
  TOOLTIP_CONTENT_STYLE,
  TOOLTIP_LABEL_STYLE,
} from "@/components/charts/theme";

export interface WaterfallItem {
  label: string;
  value: number;
  /** Render as an absolute total bar (from 0) rather than a floating delta. */
  isTotal?: boolean;
}

export interface WaterfallChartProps {
  items: ReadonlyArray<WaterfallItem>;
  baseline?: number;
  colorUp?: string;
  colorDown?: string;
  colorTotal?: string;
  height?: number;
  yWidth?: number;
  yTickFormatter?: (v: number) => string;
  valueFormatter?: (v: number) => string;
  onBarClick?: (label: string) => void;
  /** Full-screen heading (defaults to the enclosing Card's title). */
  title?: string;
  expandable?: boolean;
}

interface Row {
  label: string;
  base: number;
  delta: number;
  signed: number;
  fill: string;
}

function WaterfallChartBase({
  items,
  baseline = 0,
  colorUp = "#34d399",
  colorDown = "#f87171",
  colorTotal = "#60a5fa",
  height = 240,
  yWidth = 64,
  yTickFormatter,
  valueFormatter,
  onBarClick,
  title,
  expandable,
}: WaterfallChartProps) {
  const rows = useMemo<Row[]>(() => {
    let running = baseline;
    const out: Row[] = [];
    for (const it of items) {
      if (it.isTotal) {
        out.push({ label: it.label, base: 0, delta: Math.abs(it.value), signed: it.value, fill: colorTotal });
        running = it.value;
      } else {
        const start = running;
        const end = running + it.value;
        out.push({
          label: it.label,
          base: Math.min(start, end),
          delta: Math.abs(it.value),
          signed: it.value,
          fill: it.value >= 0 ? colorUp : colorDown,
        });
        running = end;
      }
    }
    return out;
  }, [items, baseline, colorUp, colorDown, colorTotal]);

  const fmt = valueFormatter ?? ((v: number) => v.toLocaleString("en-IN"));

  return (
    <ChartFrame height={height} title={title} expandable={expandable}>
      <BarChart data={rows} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
        <CartesianGrid {...GRID_PROPS} />
        <XAxis dataKey="label" tick={{ ...AXIS_TICK, fontSize: 10 }} tickLine={false} interval={0} angle={-25} textAnchor="end" height={60} />
        <YAxis tick={AXIS_TICK} tickLine={false} width={yWidth} tickFormatter={yTickFormatter} />
        <Tooltip
          cursor={{ fill: "rgba(255,255,255,0.04)" }}
          contentStyle={TOOLTIP_CONTENT_STYLE}
          labelStyle={TOOLTIP_LABEL_STYLE}
          formatter={((_v: number, _n: string, item: { payload?: Row }) => {
            const signed = item?.payload?.signed ?? 0;
            return [`${signed >= 0 ? "+" : ""}${fmt(signed)}`, "Contribution"] as [string, string];
          }) as never}
        />
        {/* transparent floating base */}
        <Bar dataKey="base" stackId="w" fill="transparent" isAnimationActive={false} />
        <Bar
          dataKey="delta"
          stackId="w"
          radius={[2, 2, 0, 0]}
          isAnimationActive={ANIMATION_ACTIVE}
          onClick={(d: { payload?: Row }) => onBarClick && d?.payload && onBarClick(d.payload.label)}
          cursor={onBarClick ? "pointer" : undefined}
        >
          {rows.map((r) => (
            <Cell key={r.label} fill={r.fill} />
          ))}
        </Bar>
      </BarChart>
    </ChartFrame>
  );
}

export const WaterfallChart = memo(WaterfallChartBase);
